import { useEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { THEME } from '@anytag/shared-constants';
import { useToggle } from '@anytag/shared-hooks';
import { cn, formatPercent } from '@anytag/shared-utils';
import { AccountAudienceDataRate } from '@anytag/graphql';
import {
  Icon,
  BaseModal,
  NoDataTitle,
  NoDataContentWrapper,
  ChartTooltipSingleMetric,
  NoDataContentWrapperProps
} from '@anytag/shared-atoms';
import { HighChart } from '../high-chart';
import { InfluencerAvatar } from '../influencer-avatar';

export interface AreaProvinceCityChartProps
  extends Pick<NoDataContentWrapperProps, 'header' | 'className' | 'wrapperLink' | 'wrapperText'>,
    Pick<Highcharts.ChartOptions, 'width'> {
  avatar?: string;
  hasRelativeMaxY?: boolean;
  provinceRates: readonly AccountAudienceDataRate[];
  cityRates: readonly AccountAudienceDataRate[];
}

export const AreaProvinceCityChart = ({
  width,
  header,
  avatar,
  provinceRates = [],
  cityRates = [],
  hasRelativeMaxY,
  ...noDataContentProps
}: AreaProvinceCityChartProps) => {
  const { t } = useTranslation();
  const modalState = useToggle();
  const [chartType, setChartType] = useState<'province' | 'city'>('province');

  useEffect(() => {
    if (!provinceRates.length) {
      setChartType('city');
    }
  }, []);

  const selectedData = chartType === 'province' ? provinceRates : cityRates;
  const dataNotAvailable = provinceRates.every(({ rate }) => rate === 0) && cityRates.every(({ rate }) => rate === 0);
  const { categories, seriesData } = selectedData.reduce<{ categories: string[]; seriesData: number[] }>(
    (acc, { name, rate }) => ({
      categories: [...acc.categories, name],
      seriesData: [...acc.seriesData, rate]
    }),
    { categories: [], seriesData: [] }
  );

  const chartOptions = (showAll?: boolean): Highcharts.Options => ({
    title: { text: '' },
    credits: { enabled: false },
    plotOptions: { bar: { showInLegend: false, dataLabels: { enabled: false } } },
    chart: { type: 'bar', width: showAll ? 440 : width, height: showAll ? null : 300, spacingLeft: 0 },
    xAxis: {
      title: { text: null },
      categories: showAll ? categories : categories.slice(0, 7),
      labels: {
        align: 'left',
        x: -5,
        reserveSpace: true
      }
    },
    series: [{ type: 'bar', color: '#9ea0e0', data: showAll ? seriesData : seriesData.slice(0, 7) }],
    yAxis: {
      min: 0,
      max: hasRelativeMaxY ? null : 1,
      title: { text: '' },
      labels: {
        overflow: 'justify',
        formatter() {
          return formatPercent(Number(this.value), true, 0);
        }
      }
    },
    tooltip: {
      padding: 0,
      useHTML: true,
      borderWidth: 0,
      borderRadius: 3,
      borderColor: THEME.chart.tooltip.border.colors.green,
      backgroundColor: THEME.chart.tooltip.background.colors.black,
      style: { color: THEME.chart.tooltip.text.colors.white },
      formatter() {
        return renderToStaticMarkup(
          <ChartTooltipSingleMetric
            name={t(String(this.category))}
            value={formatPercent(this.y)}
            color={this.color as string}
          />
        );
      }
    }
  });

  const RegionsCountriesTabs = (
    <div className="ml-auto flex items-center">
      <p
        onClick={() => setChartType('province')}
        className={styles.regionsCountriesTab(!provinceRates.length, chartType === 'province')}
      >
        {t('General.Provinces')}
      </p>
      <p
        onClick={() => setChartType('city')}
        className={styles.regionsCountriesTab(!cityRates.length, chartType === 'city')}
      >
        {t('General.Cities')}
      </p>
    </div>
  );

  return (
    <>
      <NoDataContentWrapper
        className="p-4"
        dataNotAvailable={dataNotAvailable}
        header={
          header ?? (
            <NoDataTitle className="mb-6" title={t('Area')}>
              {!dataNotAvailable ? RegionsCountriesTabs : null}
            </NoDataTitle>
          )
        }
        {...noDataContentProps}
      >
        <HighChart options={chartOptions()} />

        <div className="flex items-center justify-start">
          <div className="cursor-pointer leading-none" onClick={modalState.toggle}>
            <span className="text-sm font-semibold text-black-60">{t('MORE')}</span>
            <Icon icon="arrow-right" variant="blue" size="11px" className="ml-1" />
          </div>
        </div>
      </NoDataContentWrapper>

      <BaseModal
        withCloseIcon={false}
        open={modalState.on}
        onClose={modalState.setOff}
        footer={{ cancelButton: { onClick: modalState.setOff } }}
        title={
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <InfluencerAvatar src={avatar} size="24px" className="rounded-full" />
              <p className="ml-2">{t('Audience Area')}</p>
            </div>
            {RegionsCountriesTabs}
          </div>
        }
      >
        <HighChart options={chartOptions(true)} />
      </BaseModal>
    </>
  );
};

const styles = {
  regionsCountriesTab: (disabled?: boolean, active?: boolean) =>
    cn(
      'relative cursor-pointer text-base hover:opacity-80',
      'not-first-of-type:ml-4',
      'not-first-of-type:before:absolute not-first-of-type:before:top-0 not-first-of-type:before:-left-2 not-first-of-type:before:h-4.5 not-first-of-type:before:w-px not-first-of-type:before:bg-black-30 not-first-of-type:before:content-[""]',
      { 'text-blue-70': active, 'pointer-events-none opacity-50': disabled }
    )
};
